/* istanbul ignore file */
// libs
import { Routes, useParams, Navigate, useLocation } from 'react-router-dom';
// components
import { Page404 } from 'framework/statusPages';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import { ProtectedRouter } from 'infrastructure/routing';
import { Route, createRoute } from 'framework/routing';
import { GlobalProtectedRoute, ProtectedRoute } from 'framework/authorization';
// services
import EmployeesListPage from 'domain/people/employee/List';
import {
  CREATE_EMPLOYEE,
  EDIT_EMPLOYEE_PAGE_PERMISSIONS,
  GET_EMPLOYEES_LIST,
  READ_EDUCATIONS_LIST,
  READ_EMPLOYEE_COMPENSATION_PACKAGES,
  READ_EMPLOYEE_CONTRACTS,
  READ_EMPLOYEE_PERMISSION,
  READ_EMPLOYEE_QUALIFICATIONS,
  READ_EMPLOYEE_TIMELINE,
  useIsMyProfile,
  EMPLOYEES_PATH,
  EMPLOYEE_ROUTE_PATHS,
  WORKERS_PATH,
  EMPLOYEE_COMPLIANCE_READ,
} from 'domain/shared/employee';
import CreateEmployeePage from 'domain/people/employee/createupdate/Create';
import EmployeeContext, { EMPLOYEE_CONTEXT } from 'domain/people/employee/EmployeeContext';
import ShowEmployee from 'domain/people/employee/shared/ShowEmployee';
import EmployeeContractsRouter from 'domain/people/employee/contracts/EmployeeContractsRouter';
import LeaveBalanceRouter from 'domain/people/employee/leavebalance/LeaveBalancesRouter';
import FinanceRouter from 'domain/people/employee/finance/FinanceRouter';
import UpdateEmployeeProfile from 'domain/people/employee/createupdate/Update';
import EmployeeTimelinePage from 'domain/people/employee/Timeline';
import { READ_ACCESS_LOG } from 'domain/people/employee/AccessLog/accessLogService';
import AccessLogPage from 'domain/people/employee/AccessLog';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import FeedbacksRouter from './feedbacks/FeedbacksRouter';
import { FEEDBACKS_ABOUT_ME } from './feedbacks/EmployeeFeedbacksService';
import EmployeeAssignmentsRouter from './assignments/EmployeeAssignmentsRouter';
import { READ } from 'domain/shared/core/permissions';
import EmployeePerformanceReportRouter from './performanceReport/PerformanceReportRouter';
import EmployeeOfferRouter from './offers/EmployeeOfferRouter';
import { READ_EMPLOYEE_ASSIGNMENTS } from 'domain/shared/assignment';
import { READ_EMPLOYEE_COMPANY_ROLES } from './CareerCapabilities/companyRoles/shared/companyRolesService';
import EmployeeDocumentsPage from './empoyeeDocuments/EmployeeDocumentsPage';
import EmployeeLayoutWrapper from './shared/EmployeeLayoutWrapper';
import ViewEmployeeDocument from './empoyeeDocuments/view/ViewEmployeeDocument';
import GenerateEmployeeDocument from './empoyeeDocuments/createupdate/GenerateEmployeeDocument';
import EmployeeDocumentContextTemplate from './empoyeeDocuments/EmployeeDocumentContextTemplate';
import PersonalLegalPage from './PersonalLegal/PersonalLegalPage';
import EmployeeSectionWrapper from './shared/EmployeeSectionWrapper';
import { READ_EMPLOYEE_SKILLS } from './skills/useEmployeeSkillsService';
import CareerCapabilitiesPage from './CareerCapabilities/CareerCapabilitiesPage';
import EmployeeComplianceRegistry from './compliance/List/EmployeeComplianceRegistry';
import { useAuthorization } from 'framework/authorization';
import { READ_ASSESSMENTS_LIST } from './assessment/assessmentService';
import { ExpertisePermission } from './expertise/service/expertiseService.model';
import { READ_EMPLOYEE_DOCUMENTS, UPLOAD_EMPLOYEE_DOCUMENTS } from './empoyeeDocuments/constants';
import { PEOPLE_MANAGEMENT_LEAVE_BALANCE_READ } from './leavebalance/shared/constants';

export const useEmployeeId = () => {
  const isMyProfile = useIsMyProfile();

  const urlId = useParams<Dictionary<string>>().id!;
  const { getUserAlias } = useAuthenticationProvider();
  const userAlias = getUserAlias();

  return isMyProfile ? userAlias : urlId;
};

const useEmployeeRoute = () => {
  const routers = createRoute({ listRoute: WORKERS_PATH });

  // Add wildcard suffix for nested routes that need it
  const makeNestedRoute = (path: string) => `${path}/*`;

  return {
    ...routers,
    //* Personal & Legal
    profileRelativeRoute: EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL,
    newEducationRelativeRoute: `${EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL}/${makeNestedRoute(EMPLOYEE_ROUTE_PATHS.EDUCATION)}`,
    newbackgroundChecksRelativeRoute: `${EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL}/${makeNestedRoute(EMPLOYEE_ROUTE_PATHS.BACKGROUND_CHECKS)}`,
    // old
    backgroundChecksRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.BACKGROUND_CHECKS),
    educationRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.EDUCATION),
    //* end

    //* Career & Capabilities
    careerCapabilitiesRelativeRoute: EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES,
    newQualificationsRelativeRoute: `${EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES}/${makeNestedRoute(EMPLOYEE_ROUTE_PATHS.QUALIFICATIONS)}`,
    newPerformanceResultsRelativeRoute: `${EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES}/${makeNestedRoute(EMPLOYEE_ROUTE_PATHS.PERFORMANCE_DATA)}`,
    newCompanyRolesRelativeRoute: `${EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES}/${makeNestedRoute(EMPLOYEE_ROUTE_PATHS.COMPANY_ROLES)}`,
    newAssessmentsRelativeRoute: `${EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES}/${makeNestedRoute(EMPLOYEE_ROUTE_PATHS.ASSESSMENTS)}`,
    // old
    qualificationsRelativeRoute: EMPLOYEE_ROUTE_PATHS.QUALIFICATIONS,
    performanceResultsRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.PERFORMANCE_DATA),
    assessmentsRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.ASSESSMENTS),
    skillsRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.SKILLS),
    companyRolesRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.COMPANY_ROLES),
    //* end

    personalLegalEditRelativeRoute: EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL_EDIT,
    timelineRelativeRoute: `${makeNestedRoute(EMPLOYEE_ROUTE_PATHS.TIMELINE)}/:timelineEntryId?`,
    logsRelativeRoute: EMPLOYEE_ROUTE_PATHS.LOGS,
    contractsRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.CONTRACTS),
    performanceReportRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.PERFORMANCE_REPORT),

    assignmentsRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.ASSIGNMENTS),
    leavesRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.LEAVES),
    financeRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.COMPENSATION),
    offersRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.OFFERS),
    feedbacksRelativeRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.FEEDBACKS),
    documentsRoute: EMPLOYEE_ROUTE_PATHS.DOCUMENTS,
    complianceRoute: makeNestedRoute(EMPLOYEE_ROUTE_PATHS.COMPLIANCE),
  };
};

const DefaultEmployeeRedirect = () => {
  const { isGranted } = useAuthorization();
  const defaultTab = isGranted(READ_EMPLOYEE_TIMELINE, EMPLOYEE_CONTEXT)
    ? EMPLOYEE_ROUTE_PATHS.TIMELINE
    : EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL;

  return <Navigate to={defaultTab} replace />;
};

const LegacyEmployeesRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const targetPath = pathname.replace(EMPLOYEES_PATH, WORKERS_PATH);
  return <Navigate to={`${targetPath}${search}${hash}`} replace />;
};

const EmployeesRouter = () => {
  const { isAuthenticated } = useAuthenticationService();
  const {
    listRoute,
    singleEntityRoute,
    createRoute: createEmployeeRoute,

    //* Personal & Legal
    profileRelativeRoute,
    newbackgroundChecksRelativeRoute,
    newEducationRelativeRoute,
    // old
    educationRelativeRoute,
    backgroundChecksRelativeRoute,
    //* end

    //* Career & Capabilities
    careerCapabilitiesRelativeRoute,
    newPerformanceResultsRelativeRoute,
    newCompanyRolesRelativeRoute,
    newAssessmentsRelativeRoute,
    // old
    qualificationsRelativeRoute,
    skillsRelativeRoute,
    assessmentsRelativeRoute,
    performanceResultsRelativeRoute,
    companyRolesRelativeRoute,
    //* end

    financeRelativeRoute,

    feedbacksRelativeRoute,
    assignmentsRelativeRoute,
    performanceReportRelativeRoute,
    offersRelativeRoute,
    logsRelativeRoute,
    timelineRelativeRoute,
    contractsRelativeRoute,
    leavesRelativeRoute,
    personalLegalEditRelativeRoute,
    documentsRoute,
    complianceRoute,
  } = useEmployeeRoute();

  return (
    <ProtectedRouter>
      <Routes>
        <Route
          path={`${singleEntityRoute}`}
          element={
            <EmployeeContext permissionToCheck={READ_EMPLOYEE_PERMISSION}>
              <ShowEmployee>
                <EmployeeLayoutWrapper />
              </ShowEmployee>
            </EmployeeContext>
          }
        >
          {/* Personal & Legal */}
          <Route
            path={profileRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={[READ_EMPLOYEE_PERMISSION, READ_EDUCATIONS_LIST]}>
                <PersonalLegalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={newEducationRelativeRoute}
            element={<Navigate to={`../${EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL}`} replace />}
          />
          <Route
            path={newbackgroundChecksRelativeRoute}
            element={<Navigate to={`../${EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL}`} replace />}
          />
          {/* Redirects to Profile */}
          {[backgroundChecksRelativeRoute, educationRelativeRoute, 'profile'].map(path => (
            <Route
              key={path}
              path={path}
              element={<Navigate to={`../${EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL}`} replace />}
            />
          ))}
          <Route
            path="profile/edit"
            element={<Navigate to={`../${EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL_EDIT}`} replace />}
          />
          {/* end */}

          {/* Career & Capabilities */}
          <Route
            path={careerCapabilitiesRelativeRoute}
            element={
              <ProtectedRoute
                permissionToCheck={[
                  READ_EMPLOYEE_SKILLS,
                  READ_EMPLOYEE_QUALIFICATIONS,
                  READ,
                  READ_EMPLOYEE_COMPANY_ROLES,
                  READ_ASSESSMENTS_LIST,
                  ExpertisePermission.PEOPLE_MANAGEMENT_EMPLOYEE_EXPERTISE_GET,
                ]}
              >
                <CareerCapabilitiesPage />
              </ProtectedRoute>
            }
          />

          {/* Redirects to Career & Capabilities */}
          {[
            assessmentsRelativeRoute,
            companyRolesRelativeRoute,
            performanceResultsRelativeRoute,
            qualificationsRelativeRoute,
            skillsRelativeRoute,
            newPerformanceResultsRelativeRoute,
            newCompanyRolesRelativeRoute,
            newAssessmentsRelativeRoute,
          ].map(path => (
            <Route
              key={path}
              path={path}
              element={<Navigate to={`../${EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES}`} replace />}
            />
          ))}
          {/* end */}

          <Route
            path={timelineRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={READ_EMPLOYEE_TIMELINE}>
                <EmployeeSectionWrapper>
                  <EmployeeTimelinePage />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={logsRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={READ_ACCESS_LOG}>
                <EmployeeSectionWrapper>
                  <AccessLogPage />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={contractsRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={READ_EMPLOYEE_CONTRACTS}>
                <EmployeeSectionWrapper>
                  <EmployeeContractsRouter />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={documentsRoute}
            element={
              <ProtectedRoute permissionToCheck={READ_EMPLOYEE_DOCUMENTS}>
                <EmployeeSectionWrapper>
                  <EmployeeDocumentsPage />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={performanceReportRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={READ}>
                <EmployeeSectionWrapper>
                  <EmployeePerformanceReportRouter />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={assignmentsRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={READ_EMPLOYEE_ASSIGNMENTS}>
                <EmployeeSectionWrapper>
                  <EmployeeAssignmentsRouter />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path={leavesRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={PEOPLE_MANAGEMENT_LEAVE_BALANCE_READ}>
                <LeaveBalanceRouter />
              </ProtectedRoute>
            }
          />

          <Route
            path={financeRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={READ_EMPLOYEE_COMPENSATION_PACKAGES}>
                <EmployeeSectionWrapper>
                  <FinanceRouter />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={offersRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={READ}>
                <EmployeeSectionWrapper>
                  <EmployeeOfferRouter />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={feedbacksRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={FEEDBACKS_ABOUT_ME}>
                <EmployeeSectionWrapper>
                  <FeedbacksRouter />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={personalLegalEditRelativeRoute}
            element={
              <ProtectedRoute permissionToCheck={EDIT_EMPLOYEE_PAGE_PERMISSIONS}>
                <EmployeeSectionWrapper>
                  <UpdateEmployeeProfile />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path={complianceRoute}
            element={
              <ProtectedRoute permissionToCheck={EMPLOYEE_COMPLIANCE_READ}>
                <EmployeeSectionWrapper>
                  <EmployeeComplianceRegistry />
                </EmployeeSectionWrapper>
              </ProtectedRoute>
            }
          />

          <Route index element={<DefaultEmployeeRedirect />} />
        </Route>

        <Route
          path={`${singleEntityRoute}/${EMPLOYEE_ROUTE_PATHS.DOCUMENTS}/generate/:documentPath`}
          element={
            <EmployeeContext permissionToCheck={UPLOAD_EMPLOYEE_DOCUMENTS}>
              <EmployeeDocumentContextTemplate>
                <GenerateEmployeeDocument />
              </EmployeeDocumentContextTemplate>
            </EmployeeContext>
          }
        />

        <Route
          path={`${singleEntityRoute}/${EMPLOYEE_ROUTE_PATHS.DOCUMENTS}/:documentPath`}
          element={
            <EmployeeContext permissionToCheck={READ_EMPLOYEE_DOCUMENTS}>
              <EmployeeDocumentContextTemplate>
                <ViewEmployeeDocument />
              </EmployeeDocumentContextTemplate>
            </EmployeeContext>
          }
        />

        <Route
          path={listRoute}
          element={
            <GlobalProtectedRoute globalPermissionToCheck={GET_EMPLOYEES_LIST}>
              <EmployeesListPage />
            </GlobalProtectedRoute>
          }
        />

        <Route
          path={createEmployeeRoute}
          element={
            <GlobalProtectedRoute globalPermissionToCheck={CREATE_EMPLOYEE}>
              <CreateEmployeePage />
            </GlobalProtectedRoute>
          }
        />
        <Route path={`${EMPLOYEES_PATH}/*`} element={<LegacyEmployeesRedirect />} />

        <Route path="*" element={<Page404 isAuthenticated={isAuthenticated} />} />
      </Routes>
    </ProtectedRouter>
  );
};

EmployeesRouter.getRouterPath = () => `${WORKERS_PATH}/*`;

export default EmployeesRouter;
